import React, { useMemo } from "react";

import Charts from "@/components/charts";
import { formatDate, formatNumber } from "@/i18n";

const LINE_COLORS = [
  "var(--color-primary)",
  "var(--color-orange-400)",
  "var(--color-brand-400)",
  "var(--color-success)",
  "var(--color-destructive)",
];

const getValueFormatter = (valueFormat) => {
  switch (valueFormat?.type) {
    case "currency":
      return (value) =>
        formatNumber(value, {
          style: "currency",
          currency: valueFormat.currency,
          notation: "compact",
          maximumFractionDigits: 2,
        });
    case "percent":
      return (value) =>
        formatNumber(value / 100, {
          style: "percent",
          maximumFractionDigits: 2,
        });
    default:
      return (value) =>
        formatNumber(value, {
          notation: "compact",
          maximumSignificantDigits: 3,
        });
  }
};

const DrawChart = ({ title, series, valueFormat }) => {
  const { chartData, chartContent } = useMemo(() => {
    const pointsByTimestamp = new Map();

    series.forEach(({ points }, seriesIndex) => {
      points.forEach(({ date, value }) => {
        const timestamp = new Date(date).getTime();
        const point = pointsByTimestamp.get(timestamp) ?? { timestamp };
        point[`series-${seriesIndex}`] = value;
        pointsByTimestamp.set(timestamp, point);
      });
    });

    return {
      chartData: Array.from(pointsByTimestamp.values()).sort(
        (first, second) => first.timestamp - second.timestamp,
      ),
      chartContent: series.map(({ label }, index) => ({
        key: `series-${index}`,
        dataKey: `series-${index}`,
        name: label,
        stroke: LINE_COLORS[index],
        connectNulls: true,
      })),
    };
  }, [series]);

  const formatValue = getValueFormatter(valueFormat);

  return (
    <div className="w-full max-w-3xl min-w-0">
      <Charts
        chartType="line"
        title={title}
        width="100%"
        height={280}
        chartData={chartData}
        chartContent={chartContent}
        chartProps={{
          propsYAxis: { tickFormatter: formatValue },
          propsTooltip: {
            formatter: (value, name) => [formatValue(value), name],
            labelFormatter: (timestamp) =>
              formatDate(timestamp, {
                year: "numeric",
                month: "short",
                day: "numeric",
              }),
          },
        }}
      />
    </div>
  );
};

export { DrawChart };
